import { c as _c } from "react/compiler-runtime";
import { scroll, useMotionValue, useVelocity, useSpring, useTransform, type MotionValue } from "@/hooks/useFramerMotion";
import { createContext, useContext, useLayoutEffect } from "react";
import { connectScrollVelocityInput } from "./scrollVelocityInput";
import { connectDocumentScrollY } from "./useDocumentScrollY";

// Undefined: DOM consumers share the document input. Explicit null: retain
// the 3D canvas's independent-subscription diagnostic reference mode.
export const ScrollVelocitySourceContext = createContext<MotionValue<number> | null | undefined>(undefined);
export const useScrollVelocity = () => {
  const $ = _c(12);
  const source = useContext(ScrollVelocitySourceContext);
  const scrollY = useMotionValue(0);
  let t0;
  let t1;
  if ($[0] !== scrollY || $[1] !== source) {
    t0 = () => {
      if (source === undefined) {
        return connectDocumentScrollY(scrollY);
      }
      return connectScrollVelocityInput(scrollY, source, _temp);
    };
    t1 = [scrollY, source];
    $[0] = scrollY;
    $[1] = source;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useLayoutEffect(t0, t1);
  const scrollVelocity = useVelocity(scrollY);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      stiffness: 100,
      damping: 30
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const smoothVelocity = useSpring(scrollVelocity, t2);
  let t3;
  let t4;
  let t5;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = [0, 1000];
    t4 = [0, 5];
    t5 = {
      clamp: false
    };
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
  }
  const velocityFactor = useTransform(smoothVelocity, t3, t4, t5);
  let t6;
  if ($[8] !== scrollVelocity || $[9] !== smoothVelocity || $[10] !== velocityFactor) {
    t6 = {
      scrollVelocity,
      smoothVelocity,
      velocityFactor
    };
    $[8] = scrollVelocity;
    $[9] = smoothVelocity;
    $[10] = velocityFactor;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
function _temp(update) {
  return scroll((_progress, t0) => {
    const {
      y
    } = t0;
    return update(y.current);
  });
}